import { useCallback, useEffect, useState } from 'react';
import { leadgenApi, type LeadGenReportsBundle } from '@/api/leadgen.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { showApiError } from '@/utils/apiError';

function ReportTable({ title, rows }: { title: string; rows: Record<string, unknown>[] }) {
  const { rows: paginatedRows, tablePaginationProps: paginationProps } = useClientPaginatedRows(rows);

  if (!rows.length) {
    return (
      <div className="card p-5">
        <h3 className="font-bold text-slate-800 mb-2">{title}</h3>
        <p className="text-xs text-slate-400">No data available.</p>
      </div>
    );
  }
  const columns = Object.keys(rows[0]);
  return (
    <div className="card p-5 overflow-x-auto">
      <h3 className="font-bold text-slate-800 mb-4">{title}</h3>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-slate-500 border-b">
            {columns.map((c) => <th key={c} className="py-2 pr-4">{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {paginatedRows.map((row, i) => (
            <tr key={i} className="border-b border-slate-50">
              {columns.map((c) => <td key={c} className="py-2 pr-4">{String(row[c] ?? '')}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
      <TablePagination {...paginationProps} hasRows={rows.length > 0} />
    </div>
  );
}

export function LeadGenReportsPage() {
  const [reports, setReports] = useState<LeadGenReportsBundle | null>(null);

  const load = useCallback(async () => {
    try {
      setReports(await leadgenApi.getReports());
    } catch (err) {
      showApiError(err, 'Failed to load reports');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="page-content">
      <PageHeader
        title="Lead Gen Reports"
        description="Lead source, campaign, conversion, scoring, attribution, and ROI analytics."
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Lead Gen', to: '/lead-gen' },
          { label: 'Reports' },
        ]}
      />
      {reports && (
        <div className="page-content">
          <ReportTable title="Lead Source Report" rows={reports.leadSourceReport} />
          <ReportTable title="Campaign Report" rows={reports.campaignReport} />
          <ReportTable title="Conversion Report" rows={reports.conversionReport} />
          <ReportTable title="Scoring Report" rows={reports.scoringReport} />
          <ReportTable title="Attribution Report" rows={reports.attributionReport} />
          <ReportTable title="ROI Report" rows={reports.roiReport} />
        </div>
      )}
    </div>
  );
}
